import React from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const About = () => {
  const [storyRef, storyVisible] = useScrollAnimation({ threshold: 0.2, once: true });
  const [valuesRef, valuesVisible] = useScrollAnimation({ threshold: 0.1, once: true });
  const [approachRef, approachVisible] = useScrollAnimation({ threshold: 0.2, once: true });
  const [ctaRef, ctaVisible] = useScrollAnimation({ threshold: 0.3, once: true });

  const values = [
    { title: 'Excellence', description: 'We pursue the highest standards in every engagement, delivering exceptional results through meticulous attention to detail.' },
    { title: 'Integrity', description: 'Our practice is built on unwavering ethical principles and transparent communication with our clients.' },
    { title: 'Innovation', description: 'We embrace creative problem-solving and leverage cutting-edge legal strategies to achieve optimal outcomes.' },
    { title: 'Client-Centric', description: 'Your success is our priority. We tailor our approach to align with your unique objectives and business strategy.' }
  ];

  return (
    <div className="bg-cream text-ink">
      <Helmet>
        <title>About Us | Valentis Law Group & Malhotra Law Partners</title>
        <meta name="description" content="Valentis Law Group, part of Malhotra Law Partners, is a cross-border law firm with a dedicated desk in Dubai, serving clients across India, the Middle East, and Asia in dispute resolution, arbitration, and corporate advisory." />
        <link rel="canonical" href="https://valentislawgroup.com/about" />
        <meta property="og:title" content="About Us | Valentis Law Group & Malhotra Law Partners" />
        <meta property="og:description" content="Valentis Law Group, part of Malhotra Law Partners, is a cross-border law firm with a dedicated desk in Dubai, serving clients across India, the Middle East, and Asia in dispute resolution, arbitration, and corporate advisory." />
        <meta property="og:url" content="https://valentislawgroup.com/about" />
        <meta name="twitter:title" content="About Us | Valentis Law Group & Malhotra Law Partners" />
        <meta name="twitter:description" content="Valentis Law Group, part of Malhotra Law Partners, is a cross-border law firm with a dedicated desk in Dubai, serving clients across India, the Middle East, and Asia in dispute resolution, arbitration, and corporate advisory." />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-40 pb-24 lg:pt-48 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-4xl">
            <p className="text-sm uppercase tracking-widest text-ink/60 mb-6">About VALENTIS</p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-wide leading-tight mb-8">
              International Dispute Resolution
            </h1>
            <p className="text-xl text-ink/70 leading-relaxed">
              Valentis Law Group is a cross border law firm, with a dedicated desk in Dubai, focused on international dispute resolution and enforcement work involving India, China, and other Asian and Middle Eastern markets.
            </p>
          </div>
        </div>
      </section>

      {/* About the Firm */}
      <section ref={storyRef} className="py-24 lg:py-32 border-t border-ink/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className={`transition-all duration-1000 ${storyVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
              <h2 className="font-serif text-4xl md:text-5xl tracking-wide mb-8">About The Firm</h2>
              <div className="space-y-6 text-ink/70 text-lg leading-relaxed">
                <p>
                  Valentis Law Group is a cross border law firm, with a dedicated desk in Dubai, focused on international dispute resolution and enforcement work involving India, China, and other Asian and Middle Eastern markets.
                </p>
                <p>
                  The firm primarily supports businesses based in foreign jurisdictions, Non Resident Indians, expatriates, and international corporate clients who require litigation strategy and execution support in foreign courts and regulatory forums.
                </p>
                <p>
                  The firm works extensively on matters involving recovery claims, commercial disputes, contractual enforcement, financial exposure, and regulatory litigation, especially where counterparties or assets are located in India.
                </p>
              </div>
            </div>

            <div className={`bg-ink/5 backdrop-blur-sm border border-ink/10 p-12 hover:bg-ink/10 transition-all duration-700 ${storyVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
              <div className="space-y-8 text-center">
                <div className={`transition-all duration-700 ${storyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                  <svg className="w-24 h-24 text-ink mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                  <h3 className="font-serif text-5xl mb-4">Multiple Landmark Cases</h3>
                  <p className="text-ink/60 text-lg leading-relaxed">Successfully handled numerous high-value recovery cases, RERA disputes, and cheque bounce matters.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Malhotra Law Partners */}
      <section className="py-24 lg:py-32 border-t border-ink/10 bg-sand-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-16">
            <p className="text-sm uppercase tracking-[0.3em] text-ink/60 mb-4">Valentis Law Group, part of</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-wide leading-tight mb-4">Malhotra Law Partners</h2>
            <p className="font-serif text-xl md:text-2xl text-ink/60">A Cross-Border Legal Partnership</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-3 space-y-6 text-ink/70 text-lg leading-relaxed">
              <p>
                Malhotra Law Partners, a decade-old law firm based in Gurugram, has established itself as a leading legal advisor for Indian and international clients, handling complex matters across the EV sector, estate planning, IT, real estate, trading, cryptocurrency, ML research companies, HFT firms, educational institutions and financial institutions. Its practice reaches international markets including the US (New York, New Jersey, Texas), London, Singapore, the BVI, the UAE, Estonia and Australia.
              </p>
              <div className="pt-2">
                <Button
                  asChild
                  className="bg-ink text-cream hover:bg-ink/90 font-medium tracking-wider transition-all duration-300 hover:scale-105"
                >
                  <Link to="/practice-areas">
                    Explore Our Practice Areas
                    <ArrowRight className="ml-2" size={18} />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-2 grid grid-cols-2 gap-px bg-ink/20 border border-ink/20 shadow-lg">
              {[
                { value: '250+', label: 'Lawyers in our Pan-India Network' },
                { value: '10+', label: 'Years of Legal Practice' },
                { value: '7', label: 'Countries We Represent Clients In' },
                { value: '9', label: 'Specialised Industry Sectors' }
              ].map((stat) => (
                <div key={stat.label} className="bg-ink text-cream px-6 py-10 text-center">
                  <div className="font-serif text-4xl md:text-5xl mb-3">{stat.value}</div>
                  <div className="text-[11px] uppercase tracking-wider text-cream/70 leading-snug">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section ref={valuesRef} className="py-24 lg:py-32 bg-sand-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`text-center mb-16 transition-all duration-1000 ${valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <p className="text-sm uppercase tracking-widest text-ink/60 mb-4">What Drives Us</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-wide">Our Core Values</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className={`bg-cream border border-ink/10 p-10 hover:border-ink/30 hover:scale-105 transition-all duration-700 group ${valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                  }`}
                style={{ transitionDelay: valuesVisible ? `${index * 0.15}s` : '0s' }}
              >
                <CheckCircle2 className="text-ink mb-4 group-hover:scale-110 transition-transform duration-300" size={32} />
                <h3 className="font-serif text-3xl mb-4">{value.title}</h3>
                <p className="text-ink/60 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Profile */}
      <section className="py-24 lg:py-32 border-t border-ink/10 bg-sand-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-ink/60 mb-4">Leadership</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-wide">Meet Our Partner</h2>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="bg-cream border border-ink/10 overflow-hidden hover:border-ink/30 transition-all duration-500">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                {/* Profile Image */}
                <div className="relative h-96 lg:h-auto bg-gradient-to-br from-ink/10 to-ink/5">
                  <img
                    src="/Daggar Malhotra.jpg"
                    alt="Daggar Malhotra"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Profile Details */}
                <div className="p-10 lg:p-12 space-y-6">
                  <div>
                    <h3 className="font-serif text-4xl mb-3">Daggar Malhotra</h3>
                    <p className="text-lg text-ink/70 mb-2">Partner, Valentis Law Group</p>
                    <p className="text-sm text-ink/60">Gurugram, Haryana, India</p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-ink/40 mb-2">Specialization</p>
                      <p className="text-ink/80">High-Stakes Litigation & Complex Disputes</p>
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-widest text-ink/40 mb-2">Education</p>
                      <p className="text-ink/80 text-sm">Harvard University - Finance & Law</p>
                      <p className="text-ink/60 text-sm">Bachelor of Laws (LLB) - Guru Gobind Singh Indraprastha University</p>
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-widest text-ink/40 mb-2">About</p>
                      <p className="text-ink/70 text-sm leading-relaxed">
                        Daggar Malhotra is a founding partner at Valentis Law Group and a Supreme Court practising advocate known for handling complex, high-stakes disputes across India. She appears before the Supreme Court of India, the Delhi High Court, CAT Delhi, DRT Delhi, and Consumer Commissions, and is a member of both the Supreme Court Bar Association and the Delhi High Court Bar Association. With training in Finance and Law from Harvard University and an LLB from Guru Gobind Singh Indraprastha University, she brings sharp strategy, disciplined advocacy, and a results-focused approach to litigation for businesses, investors, and high-net-worth clients.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Reach (Replaces Approach) */}
      <section ref={approachRef} className="py-24 lg:py-32 border-t border-ink/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`max-w-4xl mx-auto text-center transition-all duration-1000 ${approachVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-wide mb-8">Our Reach</h2>
            <p className="text-xl text-ink/70 leading-relaxed mb-12">
              Our global footprint spans the UAE, USA, Canada, Europe, and China, with a specialized stronghold in India-related trading corridors.
            </p>
            <div className="space-y-6 text-ink/70 text-lg leading-relaxed text-left">
              <p>
                We are dedicated to bridging legal boundaries for international clients. Our expertise is deeply rooted in cross-border commercial litigation, including the recovery of money and goods, cheque bounce cases, and the execution of foreign judgments.
              </p>
              <p>
                Beyond corporate needs, we provide comprehensive support for private matters, expertly navigating RERA disputes, NRI family law, and complex succession and inheritance proceedings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section ref={ctaRef} className="py-24 lg:py-32 bg-sand-50">
        <div className={`max-w-4xl mx-auto px-6 lg:px-12 text-center transition-all duration-1000 ${ctaVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <h2 className="font-serif text-4xl md:text-5xl tracking-wide mb-8">Join Our Legacy of Success</h2>
          <p className="text-xl text-ink/70 mb-12">
            Experience the VALENTIS difference. Let's discuss how we can support your legal needs.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-ink text-cream hover:bg-ink/90 px-8 py-6 text-base font-medium tracking-wider transition-all duration-300 hover:scale-110 hover:shadow-2xl"
          >
            <Link to="/contact">Contact Us Today</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default About;